import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-US", { timeZone: "America/Los_Angeles", dateStyle: "medium", timeStyle: "short" }) : "—");

export default function StaffPeople() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [staffOnly, setStaffOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [roles, setRoles] = useState([]);
  const isSysAdmin = user.roles.includes("system_admin");

  const { data: people = [] } = useQuery({ queryKey: ["admin", "users", q, staffOnly], queryFn: async () => (await api.get("/admin/users", { params: { q: q || undefined, staff_only: staffOnly } })).data });
  const { data: roleList = [] } = useQuery({ queryKey: ["admin", "roles"], queryFn: async () => (await api.get("/admin/roles")).data });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "users"] });

  const act = useMutation({
    mutationFn: ({ id, action }) => api.post(`/admin/users/${id}/${action}`),
    onSuccess: () => { invalidate(); toast.success("Saved."); },
    onError: (e) => toast.error(apiError(e)),
  });
  const saveRoles = useMutation({
    mutationFn: () => api.patch(`/admin/users/${editing.id}/roles`, { roles }),
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Roles updated."); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div data-testid="staff-people">
      <h1 className="text-4xl">People, Roles & Credentials</h1>
      <p className="mt-3 text-ink-500 max-w-[65ch]">Every account, with roles, MFA status, and standing. Counselor credentials live under <Link to="/staff/providers" className="link">Counselors & Credentials</Link>. {isSysAdmin ? "" : "Only a System Admin can change roles or deactivate accounts."}</p>
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-end">
        <div className="flex-1 max-w-md">
          <label htmlFor="people-search" className="label">Search</label>
          <input id="people-search" className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or email" data-testid="people-search" />
        </div>
        <label className="tap justify-start gap-3 cursor-pointer"><input type="checkbox" className="h-5 w-5 accent-[#212B23]" checked={staffOnly} onChange={(e) => setStaffOnly(e.target.checked)} data-testid="people-staff-only" /> Staff only</label>
      </div>
      <div className="mt-8 overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="people-table">
          <thead className="border-b-2 border-sage-400 text-ink-500"><tr><th className="py-2 pr-4 font-semibold">Name</th><th className="py-2 pr-4 font-semibold">Email</th><th className="py-2 pr-4 font-semibold">Roles</th><th className="py-2 pr-4 font-semibold">MFA</th><th className="py-2 pr-4 font-semibold">Verified</th><th className="py-2 pr-4 font-semibold">Status</th><th className="py-2 pr-4 font-semibold">Last Sign-In</th>{isSysAdmin && <th className="py-2 font-semibold">Actions</th>}</tr></thead>
          <tbody className="divide-y divide-sage-400/50">
            {people.map((p) => (
              <tr key={p.id} data-testid={`person-row-${p.id}`} className={p.status !== "active" ? "text-ink-500" : ""}>
                <td className="py-3 pr-4 font-semibold text-base">{p.first_name} {p.last_name}</td>
                <td className="py-3 pr-4">{p.email}</td>
                <td className="py-3 pr-4"><div className="flex flex-wrap gap-1">{p.roles.map((r) => <span key={r} className="rounded border border-sage-400 bg-cream-100 px-2 py-0.5 text-xs">{roleList.find((x) => x.key === r)?.label || r}</span>)}</div></td>
                <td className="py-3 pr-4">{p.mfa_enabled ? "On" : "Off"}</td>
                <td className="py-3 pr-4">{p.email_verified ? "Yes" : "No"}</td>
                <td className="py-3 pr-4">{p.status === "active" ? "Active" : <span className="text-terracotta-600 font-semibold">Deactivated</span>}</td>
                <td className="py-3 pr-4 whitespace-nowrap">{fmt(p.last_login_at)}</td>
                {isSysAdmin && (
                  <td className="py-3">
                    <div className="flex flex-wrap gap-1">
                      <button type="button" className="btn-quiet min-h-[36px] px-2 text-sm" onClick={() => { setEditing(p); setRoles(p.roles); }} data-testid={`edit-roles-${p.id}`}>Roles</button>
                      {p.mfa_enabled && <button type="button" className="btn-quiet min-h-[36px] px-2 text-sm" onClick={() => window.confirm("Reset MFA for this person? This is audit-logged.") && act.mutate({ id: p.id, action: "mfa-reset" })} data-testid={`mfa-reset-${p.id}`}>Reset MFA</button>}
                      {p.id !== user.id && (p.status === "active"
                        ? <button type="button" className="btn-quiet min-h-[36px] px-2 text-sm text-terracotta-600" onClick={() => window.confirm("Deactivate this account?") && act.mutate({ id: p.id, action: "deactivate" })} data-testid={`deactivate-${p.id}`}>Deactivate</button>
                        : <button type="button" className="btn-quiet min-h-[36px] px-2 text-sm" onClick={() => act.mutate({ id: p.id, action: "reactivate" })} data-testid={`reactivate-${p.id}`}>Reactivate</button>)}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-cream-50" data-testid="roles-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Edit Roles</DialogTitle><DialogDescription>{editing?.first_name} {editing?.last_name} · {editing?.email}</DialogDescription></DialogHeader>
          <div className="grid gap-2 sm:grid-cols-2">
            {roleList.map((r) => (
              <label key={r.key} className="tap justify-start gap-3 rounded border border-sage-400 bg-cream-100 px-3 cursor-pointer has-[:checked]:border-olive-900">
                <input type="checkbox" className="h-5 w-5 accent-[#212B23]" checked={roles.includes(r.key)} onChange={(e) => setRoles(e.target.checked ? [...roles, r.key] : roles.filter((x) => x !== r.key))} data-testid={`role-checkbox-${r.key}`} />
                <span>{r.label}</span>
              </label>
            ))}
          </div>
          <div className="mt-4 flex gap-3"><button type="button" className="btn-primary" onClick={() => saveRoles.mutate()} disabled={roles.length === 0} data-testid="roles-save">Save Roles</button><button type="button" className="btn-secondary" onClick={() => setEditing(null)}>Cancel</button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
